import { db } from '../firebase';
import firebase from 'firebase/app';

export async function getUsers({
  page = 1,
  pageSize = 20,
  search = '',
  versionFilter = 'all',
  platformFilter = 'all',
  activityFilter = 'all',
  startDate = null,
  endDate = null,
} = {}) {
  try {
    const snapshot = await db.collection('users').get();
    let allUsers = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    const startOfYesterday = new Date(startOfToday.getTime() - 86400000);
    const endOfYesterday = new Date(startOfToday.getTime() - 1);
    const sevenDaysAgo = new Date(startOfToday.getTime() - 6 * 86400000);
    const thirtyDaysAgo = new Date(startOfToday.getTime() - 29 * 86400000);

    const getActivityMillis = (u) => {
      const val = u.lastActive || u.lastOpened || u.lastTokenUpdated;
      if (!val) return 0;
      if (val.toDate) return val.toDate().getTime();
      const d = new Date(val);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    };

    const getCreatedMillis = (u) => {
      const val = u.createdAt;
      if (!val) return 0;
      if (val.toDate) return val.toDate().getTime();
      const d = new Date(val);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    };

    // Sort by activity date descending (prioritize users active recently, then creation date)
    allUsers.sort((a, b) => {
      const aTime = getActivityMillis(a) || getCreatedMillis(a);
      const bTime = getActivityMillis(b) || getCreatedMillis(b);
      return bTime - aTime;
    });

    // Apply activity / app-open filter
    if (activityFilter && activityFilter !== 'all') {
      if (activityFilter === 'daily' || activityFilter === 'today') {
        allUsers = allUsers.filter((u) => {
          const t = getActivityMillis(u);
          return t >= startOfToday.getTime() && t <= endOfToday.getTime();
        });
      } else if (activityFilter === 'yesterday') {
        allUsers = allUsers.filter((u) => {
          const t = getActivityMillis(u);
          return t >= startOfYesterday.getTime() && t <= endOfYesterday.getTime();
        });
      } else if (activityFilter === 'last7Days') {
        allUsers = allUsers.filter((u) => {
          const t = getActivityMillis(u);
          return t >= sevenDaysAgo.getTime();
        });
      } else if (activityFilter === 'last30Days') {
        allUsers = allUsers.filter((u) => {
          const t = getActivityMillis(u);
          return t >= thirtyDaysAgo.getTime();
        });
      } else if (activityFilter === 'custom') {
        let sTime = 0;
        let eTime = Infinity;
        if (startDate) {
          const s = new Date(startDate);
          sTime = new Date(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0).getTime();
        }
        if (endDate) {
          const e = new Date(endDate);
          eTime = new Date(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999).getTime();
        }
        allUsers = allUsers.filter((u) => {
          const t = getActivityMillis(u);
          return t >= sTime && t <= eTime;
        });
      }
    }

    // Apply version filter
    if (versionFilter && versionFilter !== 'all') {
      if (versionFilter === 'none') {
        allUsers = allUsers.filter((u) => !u.appVersion && !u.appVersionFull);
      } else {
        allUsers = allUsers.filter(
          (u) =>
            (u.appVersion || '') === versionFilter ||
            (u.appVersionFull || '').startsWith(versionFilter)
        );
      }
    }

    // Apply platform filter
    if (platformFilter && platformFilter !== 'all') {
      allUsers = allUsers.filter(
        (u) => (u.platform || '').toLowerCase() === platformFilter.toLowerCase()
      );
    }

    // Text search matching name, email, uid, appVersion
    if (search && search.trim()) {
      const q = search.trim().toLowerCase();
      allUsers = allUsers.filter((u) => {
        const name = (u.name || u.displayName || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        const uid = (u.id || u.uid || '').toLowerCase();
        const version = (u.appVersion || u.appVersionFull || '').toLowerCase();
        const platform = (u.platform || '').toLowerCase();
        return (
          name.includes(q) ||
          email.includes(q) ||
          uid.includes(q) ||
          version.includes(q) ||
          platform.includes(q)
        );
      });
    }

    const total = allUsers.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const validPage = Math.max(1, Math.min(page, totalPages));
    const startIndex = (validPage - 1) * pageSize;
    const items = allUsers.slice(startIndex, startIndex + pageSize);
    return { items, total, totalPages, page: validPage, pageSize };
  } catch (error) {
    console.error('Error in getUsers:', error);
    const snapshot = await db.collection('users').get();
    let allUsers = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    if (search && search.trim()) {
      const q = search.trim().toLowerCase();
      allUsers = allUsers.filter((u) => {
        const name = (u.name || u.displayName || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        const version = (u.appVersion || '').toLowerCase();
        return (
          name.includes(q) ||
          email.includes(q) ||
          version.includes(q)
        );
      });
    }
    return { items: allUsers, total: allUsers.length, totalPages: 1, page: 1, pageSize };
  }
}

export async function getUserAnalyticsBreakdown() {
  try {
    const snapshot = await db.collection('users').get();
    const users = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    const versionMap = {};
    const platformMap = { android: 0, ios: 0, web: 0, other: 0 };
    let notTrackedCount = 0;
    let fcmActiveCount = 0;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday.getTime() - 86400000);
    const sevenDaysAgo = new Date(startOfToday.getTime() - 6 * 86400000);
    const thirtyDaysAgo = new Date(startOfToday.getTime() - 29 * 86400000);

    let registeredToday = 0;
    let registeredYesterday = 0;
    let registeredLast7Days = 0;
    let registeredLast30Days = 0;
    let activeToday = 0;
    let activeLast7Days = 0;

    // Initialize 14-day trend map
    const dailyMap = {};
    for (let i = 13; i >= 0; i--) {
      const d = new Date(startOfToday.getTime() - i * 86400000);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
      dailyMap[key] = { dateStr: label, dayName, newUsers: 0, activeUsers: 0 };
    }

    users.forEach((u) => {
      // 1. App Version tracking
      if (u.appVersion) {
        const versionKey = `v${u.appVersion}`;
        versionMap[versionKey] = (versionMap[versionKey] || 0) + 1;
      } else {
        notTrackedCount += 1;
      }

      // 2. Platform tracking
      const plat = (u.platform || '').toLowerCase();
      if (plat === 'android') platformMap.android += 1;
      else if (plat === 'ios') platformMap.ios += 1;
      else if (plat === 'web') platformMap.web += 1;
      else if (plat) platformMap.other += 1;

      // 3. FCM token check
      const hasFcm = Boolean(u.fcmToken && String(u.fcmToken).trim().length > 10);
      if (hasFcm) fcmActiveCount += 1;

      // 4. Registration dates
      let cDate = null;
      const regTime = u.createdAt || u.lastActive || u.lastOpened || u.lastTokenUpdated;
      if (regTime) {
        cDate = regTime.toDate ? regTime.toDate() : new Date(regTime);
      }
      if (cDate && !isNaN(cDate.getTime())) {
        if (cDate >= startOfToday) registeredToday += 1;
        else if (cDate >= startOfYesterday && cDate < startOfToday) registeredYesterday += 1;

        if (cDate >= sevenDaysAgo) registeredLast7Days += 1;
        if (cDate >= thirtyDaysAgo) registeredLast30Days += 1;

        const dateKey = `${cDate.getFullYear()}-${String(cDate.getMonth() + 1).padStart(2, '0')}-${String(cDate.getDate()).padStart(2, '0')}`;
        if (dailyMap[dateKey]) {
          dailyMap[dateKey].newUsers += 1;
        }
      }

      // 5. Activity dates
      let aDate = null;
      if (u.lastActive) {
        aDate = u.lastActive.toDate ? u.lastActive.toDate() : new Date(u.lastActive);
      }
      if (aDate && !isNaN(aDate.getTime())) {
        if (aDate >= startOfToday) activeToday += 1;
        if (aDate >= sevenDaysAgo) activeLast7Days += 1;

        const dateKey = `${aDate.getFullYear()}-${String(aDate.getMonth() + 1).padStart(2, '0')}-${String(aDate.getDate()).padStart(2, '0')}`;
        if (dailyMap[dateKey]) {
          dailyMap[dateKey].activeUsers += 1;
        }
      }
    });

    const topVersions = Object.entries(versionMap)
      .map(([version, count]) => ({ version, count }))
      .sort((a, b) => b.count - a.count);

    const dailyTrend = Object.values(dailyMap);
    const fcmActiveRate = users.length > 0 ? Number(((fcmActiveCount / users.length) * 100).toFixed(1)) : 0;

    return {
      totalUsers: users.length,
      topVersions,
      notTrackedCount,
      platforms: platformMap,
      fcmActiveCount,
      fcmActiveRate,
      growth: {
        today: registeredToday,
        yesterday: registeredYesterday,
        last7Days: registeredLast7Days,
        last30Days: registeredLast30Days,
        activeToday,
        activeLast7Days,
      },
      dailyTrend,
    };
  } catch (error) {
    console.error('Error in getUserAnalyticsBreakdown:', error);
    return {
      totalUsers: 0,
      topVersions: [],
      notTrackedCount: 0,
      platforms: { android: 0, ios: 0, web: 0, other: 0 },
      fcmActiveCount: 0,
      fcmActiveRate: 0,
      growth: { today: 0, yesterday: 0, last7Days: 0, last30Days: 0, activeToday: 0, activeLast7Days: 0 },
      dailyTrend: [],
    };
  }
}

export async function getUserHistory(userId) {
  const snapshot = await db
    .collection('users')
    .doc(userId)
    .collection('history')
    .orderBy('viewedAt', 'desc')
    .limit(50)
    .get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export async function getTotalUsers() {
  const snapshot = await db.collection('users').get();
  return snapshot.size;
}

export async function getTodayActiveUsers() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const snapshot = await db
    .collection('users')
    .where('lastActive', '>=', today)
    .get();
  return snapshot.size;
}

export async function sendDirectNotification(userId, data) {
  const notifDoc = {
    title: data.title,
    body: data.body,
    imageUrl: data.imageUrl || null,
    actionUrl: data.actionUrl || null,
    buttonText: data.buttonText || null,
    data: {
      imageUrl: data.imageUrl || null,
      actionUrl: data.actionUrl || null,
      buttonText: data.buttonText || null,
      type: 'direct_user_message',
    },
    targetUserId: userId,
    targetUserName: data.user?.displayName || data.user?.name || 'User',
    targetUserEmail: data.user?.email || '',
    isDirect: true,
    topic: null,
    status: 'sent',
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    sentCount: 1,
  };

  // 1. Add to main notifications collection with targetUserId
  const docRef = await db.collection('notifications').add(notifDoc);

  // 2. Also write to user's private notification subcollection
  try {
    await db.collection('users').doc(userId).collection('notifications').doc(docRef.id).set({
      ...notifDoc,
      id: docRef.id,
    });
  } catch (err) {
    console.warn('Could not save to user subcollection:', err);
  }

  return docRef.id;
}

export async function sendDirectInAppMessage(userId, data) {
  const campaignDoc = {
    title_en: data.title_en || data.title || '',
    title_bn: data.title_bn || '',
    body_en: data.body_en || data.body || '',
    body_bn: data.body_bn || '',
    imageUrl: data.imageUrl || null,
    actionUrl: data.actionUrl || null,
    buttonText_en: data.buttonText_en || 'Explore Now',
    buttonText_bn: data.buttonText_bn || 'এখনই দেখুন',
    durationDays: Number(data.durationDays) || 7,
    displayFrequency: data.displayFrequency || 'once_ever',
    maxImpressions: Number(data.maxImpressions) || 1,
    targetScreen: 'all',
    targetUserId: userId,
    targetUserName: data.user?.displayName || data.user?.name || 'User',
    targetUserEmail: data.user?.email || '',
    isDirect: true,
    isActive: true,
    version: 1,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };

  const docRef = await db.collection('in_app_campaigns').add(campaignDoc);
  return docRef.id;
}

export async function deleteUser(userId) {
  try {
    // 1. Clean up subcollections (history, notifications) if any
    try {
      const historySnap = await db.collection('users').doc(userId).collection('history').get();
      if (!historySnap.empty) {
        const batch1 = db.batch();
        historySnap.docs.forEach((doc) => batch1.delete(doc.ref));
        await batch1.commit();
      }
    } catch (e) {
      console.warn('Error clearing user history subcollection:', e);
    }

    try {
      const notifsSnap = await db.collection('users').doc(userId).collection('notifications').get();
      if (!notifsSnap.empty) {
        const batch2 = db.batch();
        notifsSnap.docs.forEach((doc) => batch2.delete(doc.ref));
        await batch2.commit();
      }
    } catch (e) {
      console.warn('Error clearing user notifications subcollection:', e);
    }

    // 2. Delete the user document in 'users' collection
    await db.collection('users').doc(userId).delete();
    return true;
  } catch (error) {
    console.error('Error deleting user in userService:', error);
    throw error;
  }
}